import { PortalEditorImageInteractionConfig } from '@src/ui/Editor/constants/editorImageInteractionConfig';
import { PortalEditorObjectPosition } from '@src/ui/Editor/types';
import Button from '@src/ui/elements/button/Button';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ImageAltEditPopupProps {
  isOpen: boolean;
  position: PortalEditorObjectPosition;
  currentAlt: string;
  onUpdate: (altText: string) => void;
  onClose: () => void;
}

export const ImageAltEditPopup: React.FC<ImageAltEditPopupProps> = ({
  isOpen,
  position,
  currentAlt,
  onUpdate,
  onClose,
}) => {
  const [altText, setAltText] = useState(currentAlt);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAltText(currentAlt);
  }, [currentAlt]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, PortalEditorImageInteractionConfig.actionTimeout);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (popupRef.current?.contains(target)) {
        return;
      }
      if (target.tagName === 'IMG' && target.closest('.ProseMirror')) {
        return;
      }
      onClose();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('mousedown', handleClickOutside, true);
    }, PortalEditorImageInteractionConfig.actionTimeout);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleUpdate = () => {
    onUpdate(altText);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdate();
    }
  };

  return createPortal(
    <div
      ref={popupRef}
      className='fixed z-[9998] flex items-center gap-2 py-2'
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type='text'
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder='タイトルを入力'
        className='h-[38px] w-[400px] rounded-[2px] border border-gray-300 px-2 text-sm focus:outline-none'
      />
      <div className='w-[80px]'>
        <Button
          size='xs'
          disabled={altText === currentAlt}
          onClick={handleUpdate}
        >
          更新
        </Button>
      </div>
    </div>,
    document.body,
  );
};
